import React from 'react';
import { CheckCircle2, Building2, ShieldCheck, Users } from 'lucide-react';

const credentials = [
  {
    icon: Building2,
    title: '5+ years managing HMOs',
    body: 'Hands-on experience running multi-let properties across Kent, West London, South East London and East London — from single lets to 8-bed HMOs.',
  },
  {
    icon: ShieldCheck,
    title: 'HMO licensing & compliance',
    body: 'Deep working knowledge of Article 4, Additional & Selective Licensing schemes, fire safety, and HMO regulations.',
  },
  {
    icon: Users,
    title: 'Tenant sourcing & retention',
    body: 'Consistently keeping rooms filled with vetted professional tenants — minimising voids, maximising landlord returns.',
  },
];

const highlights = [
  'Article 4 & HMO licensing applications handled end-to-end',
  'Fire safety, gas, EICR and legionella compliance managed',
  'Room-by-room refurbishments to maximise rental yield',
  'Long-standing relationships with landlords across Kent, West, South East & East London',
  'Deep tenant vetting process — right-to-rent, referencing, deposits',
  'Direct experience with rent-to-rent, guaranteed rent and HMO management',
];

export default function Experience() {
  return (
    <section id="experience" className="relative py-24 md:py-36 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-24 items-start">
          {/* Left column */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-[#e4cfaf]" />
              <span className="uppercase text-[11px] tracking-[0.28em] text-[#e4cfaf]/90">Experience</span>
            </div>
            <h2 className="serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
              Years on the ground <span className="italic-serif">as HMO managers.</span>
            </h2>
            <p className="text-white/65 text-lg leading-relaxed mb-8 max-w-xl">
              MSR Residential Group is founded and run by Gurpreet &mdash; a hands-on HMO
              manager with a track record of running compliant, high-yielding houses in
              multiple occupation across Kent, West London, South East London and East London.
            </p>
            <p className="text-white/55 leading-relaxed max-w-xl">
              From day-to-day tenant management to licensing paperwork, refurb decisions,
              and 3AM maintenance calls &mdash; we have done every part of the operation
              ourselves. That real-world experience is what allows us to guarantee your
              rent with confidence.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6">
              <div className="border-t border-[#e4cfaf]/30 pt-4">
                <div className="serif text-[#e4cfaf] text-4xl md:text-5xl">5+</div>
                <div className="uppercase text-[10px] tracking-[0.24em] text-white/60 mt-2">
                  Years HMO experience
                </div>
              </div>
              <div className="border-t border-[#e4cfaf]/30 pt-4">
                <div className="serif text-[#e4cfaf] text-4xl md:text-5xl">100+</div>
                <div className="uppercase text-[10px] tracking-[0.24em] text-white/60 mt-2">
                  Rooms let &amp; managed
                </div>
              </div>
            </div>
          </div>

          {/* Right column - credential cards */}
          <div>
            <div className="grid gap-4">
              {credentials.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.title}
                    className="tilt-card border border-white/10 bg-[#141416] p-6 md:p-8 rounded-sm flex gap-5"
                  >
                    <div className="shrink-0 w-12 h-12 border border-[#e4cfaf]/40 rounded-sm flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#e4cfaf]" />
                    </div>
                    <div>
                      <h3 className="serif text-white text-xl md:text-2xl mb-2">{c.title}</h3>
                      <p className="text-white/60 leading-relaxed text-[15px]">{c.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 border border-white/10 bg-[#0f0f10] p-6 md:p-8 rounded-sm">
              <h4 className="serif text-white text-lg md:text-xl mb-5">
                What we handle in-house
              </h4>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-white/70 text-[14.5px] leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-[#e4cfaf] shrink-0 mt-1" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
